import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  Cog6ToothIcon,
  CloudIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  ChartBarIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const CONFIG_CARDS = [
  {
    icon: SparklesIcon,
    label: 'Branding',
    desc: 'Parent/affiliate naming, support inbox, portal URLs',
    path: '/branding',
    accent: 'text-indigo-600 bg-indigo-50 group-hover:bg-indigo-100',
    border: 'hover:border-indigo-200',
  },
  {
    icon: CloudIcon,
    label: 'Storage Configuration',
    desc: 'S3-compatible, SharePoint, or RustFS backends',
    path: '/storage-config',
    accent: 'text-violet-600 bg-violet-50 group-hover:bg-violet-100',
    border: 'hover:border-violet-200',
  },
  {
    icon: DevicePhoneMobileIcon,
    label: 'App Fleet',
    desc: 'Device versions, releases and force-update policy',
    path: '/app-versions',
    accent: 'text-emerald-600 bg-emerald-50 group-hover:bg-emerald-100',
    border: 'hover:border-emerald-200',
  },
  {
    icon: EyeIcon,
    label: 'System Monitoring',
    desc: 'Alerts, metrics and real-time service health',
    path: '/monitoring',
    accent: 'text-amber-600 bg-amber-50 group-hover:bg-amber-100',
    border: 'hover:border-amber-200',
  },
  {
    icon: ChartBarIcon,
    label: 'Reports & Analytics',
    desc: 'Platform metrics, feature adoption and exports',
    path: '/reports',
    accent: 'text-pink-600 bg-pink-50 group-hover:bg-pink-100',
    border: 'hover:border-pink-200',
  },
  {
    icon: ShieldCheckIcon,
    label: 'Audit Logs',
    desc: 'Track all administrative actions across the platform',
    path: '/reports',
    accent: 'text-slate-600 bg-slate-100 group-hover:bg-slate-200',
    border: 'hover:border-slate-300',
  },
]

export default function Settings() {
  const { user } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Cog6ToothIcon className="w-7 h-7 text-blue-600" />
            Settings
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Account information and system configuration</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-8">
        {/* Account */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <UserCircleIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-semibold text-slate-900">Account Information</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-5">
            {([
              ['Name', user?.name || '—'],
              ['Email', user?.email || '—'],
              ['Role', user?.role || 'owner'],
              ['Stack ID', user?.stackId ? user.stackId.slice(0, 14) + '…' : '—'],
            ] as [string, string][]).map(([label, value]) => (
              <div key={label}>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
                <p className="text-sm text-slate-800 font-medium truncate" title={value}>{value}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/account-settings')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
          >
            Manage Account
          </button>
        </div>

        {/* Config shortcuts */}
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-1">System Configuration</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CONFIG_CARDS.map(({ icon: Icon, label, desc, path, accent, border }) => (
              <button
                key={label}
                onClick={() => navigate(path)}
                className={`group bg-white rounded-xl border border-slate-200 shadow-sm p-5 text-left transition-all hover:shadow-md ${border}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors ${accent}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-sm font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">{label}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
